import { Button } from '@heidi/design-system/button';
import { SidebarMenu, SidebarMenuItem } from '@heidi/design-system/page-shell';
import { TypographyCaption } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Loader2 } from 'lucide-react';
import type { JSX, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import type { Workflow } from '../../../contracts/routines.types';
import { workflowsSidebarSectionMessages as m } from '../../../data/workflows/components/WorkflowsSidebarSection.messages';

/** Compact row shown while `useWorkflows`/`useWorkflowSchedules` are still resolving. */
export function WorkflowsSidebarLoadingState(): JSX.Element {
  return (
    <div
      className="flex h-8 items-center gap-1.5 px-2 py-1 text-text-tertiary"
      data-testid="sidebar-routines-loading"
    >
      <Loader2 aria-hidden className="size-3.5 shrink-0 animate-spin" />
      <TypographyCaption>
        <FormattedMessage {...m.loading} />
      </TypographyCaption>
    </div>
  );
}

/** Compact row shown when either query failed — no raw error text, ever. */
export function WorkflowsSidebarErrorState(): JSX.Element {
  return (
    <TypographyCaption
      className="block px-2 py-1 text-text-tertiary"
      data-testid="sidebar-routines-error"
    >
      <FormattedMessage {...m.loadError} />
    </TypographyCaption>
  );
}

type RoutinesShowMoreButtonProps = {
  showAll: boolean;
  hiddenCount: number;
  isSidebarPanelEnabled: boolean;
  onToggle: () => void;
  className: string;
};

// This DS compliance fix intentionally ships outside the scheduled-only kill switch.
function RoutinesShowMoreButton({
  showAll,
  hiddenCount,
  isSidebarPanelEnabled,
  onToggle,
  className,
}: RoutinesShowMoreButtonProps): JSX.Element {
  return (
    <Button
      type="button"
      variant="link"
      size="xs"
      disableAutomaticTypographyClasses
      onClick={onToggle}
      className={cn(
        className,
        'justify-start',
        isSidebarPanelEnabled
          ? 'text-text-secondary hover:bg-background-secondary hover:text-text-primary'
          : 'text-text-secondary hover:bg-background-secondary'
      )}
    >
      <TypographyCaption>
        {showAll ? (
          <FormattedMessage {...m.showLess} />
        ) : (
          <FormattedMessage {...m.showMore} values={{ count: hiddenCount }} />
        )}
      </TypographyCaption>
    </Button>
  );
}

/** Per-row selection/attention/loading state, computed here from the list-level props. */
export type WorkflowsSidebarRoutineRowMeta = {
  selected: boolean;
  needsAttention: boolean;
  isOpening: boolean;
};

/**
 * Renders one routine row. The row markup itself (`NavItem`, the Chat-sidebar
 * button, `SidebarNavRowStatusIcon`) is app-only sidebar chrome, so the host
 * supplies it — this package only computes which state each row is in.
 */
export type WorkflowsSidebarRoutineRowRenderer = (
  workflow: Workflow,
  meta: WorkflowsSidebarRoutineRowMeta
) => ReactNode;

export type WorkflowsSidebarRoutineListsProps = {
  visibleWorkflows: Workflow[];
  focusedWorkflowId: string | null;
  focusedFromProject: string | null;
  currentChatId: string | null;
  attentionWorkflowIds: ReadonlySet<string>;
  openingId: string | null;
  isSidebarPanelEnabled: boolean;
  canToggle: boolean;
  showAll: boolean;
  hiddenCount: number;
  onToggleShowAll: () => void;
  /** App-side row renderer for the denser Agent Home `NavItem` presentation. */
  renderNavItemRow: WorkflowsSidebarRoutineRowRenderer;
  /** App-side row renderer for the Chat-sidebar button presentation. */
  renderChatRow: WorkflowsSidebarRoutineRowRenderer;
};

const rowMeta = (
  workflow: Workflow,
  {
    focusedWorkflowId,
    attentionWorkflowIds,
    openingId,
  }: Pick<
    WorkflowsSidebarRoutineListsProps,
    'focusedWorkflowId' | 'attentionWorkflowIds' | 'openingId'
  >
): WorkflowsSidebarRoutineRowMeta => ({
  selected: workflow.workflow_id === focusedWorkflowId,
  needsAttention: attentionWorkflowIds.has(workflow.workflow_id),
  isOpening: openingId === workflow.workflow_id,
});

export function AgentHomeRoutinesList({
  visibleWorkflows,
  focusedWorkflowId,
  attentionWorkflowIds,
  openingId,
  isSidebarPanelEnabled,
  canToggle,
  showAll,
  hiddenCount,
  onToggleShowAll,
  renderNavItemRow,
}: WorkflowsSidebarRoutineListsProps): JSX.Element {
  return (
    <SidebarMenu>
      {visibleWorkflows.map((workflow) =>
        renderNavItemRow(
          workflow,
          rowMeta(workflow, {
            focusedWorkflowId,
            attentionWorkflowIds,
            openingId,
          })
        )
      )}
      {canToggle ? (
        <SidebarMenuItem>
          <RoutinesShowMoreButton
            showAll={showAll}
            hiddenCount={hiddenCount}
            isSidebarPanelEnabled={isSidebarPanelEnabled}
            onToggle={onToggleShowAll}
            className="flex h-8 w-full items-center rounded-[10px] px-3 py-1 text-start"
          />
        </SidebarMenuItem>
      ) : null}
    </SidebarMenu>
  );
}

export function ChatRoutinesList({
  visibleWorkflows,
  focusedWorkflowId,
  attentionWorkflowIds,
  openingId,
  isSidebarPanelEnabled,
  canToggle,
  showAll,
  hiddenCount,
  onToggleShowAll,
  renderChatRow,
}: WorkflowsSidebarRoutineListsProps): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5 pb-1">
      {visibleWorkflows.map((workflow) =>
        renderChatRow(
          workflow,
          rowMeta(workflow, {
            focusedWorkflowId,
            attentionWorkflowIds,
            openingId,
          })
        )
      )}
      {canToggle ? (
        <RoutinesShowMoreButton
          showAll={showAll}
          hiddenCount={hiddenCount}
          isSidebarPanelEnabled={isSidebarPanelEnabled}
          onToggle={onToggleShowAll}
          className="flex h-8 w-full items-center rounded-md px-2 py-1 text-start"
        />
      ) : null}
    </div>
  );
}
